import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Divider,
  Box,
} from "@mui/material";

/* ================================
   Helpers
================================ */
const fmtMoney = (n) => `৳ ${Number(n || 0).toLocaleString("en-US")}`;

/**
 * Maps the raw form data to readable labels for the UI
 */
const getDisplayFields = (item) => {


  const d = item.data || {};

  //alert(item.data);


// Handle Motor Liability Insurance (Third Party)
  if (item.productType === "Motor Liability Insurance") {
    const b = d.premiumBreakdown || {};
    return [
      { label: "Owner Name", value: d.ownerName },
      { label: "Vehicle Number", value: d.vehicleNumber },
      { label: "Vehicle Type", value: d.vehicleType?.toUpperCase() },
      { label: "Chassis Number", value: d.chassisNumber },
      { label: "Engine Number", value: d.engineNumber },
      { label: "Mfg Year", value: d.mfgYear },
      { label: "Policy Start Date", value: d.policyStartDate },
      { label: "Basic Premium", value: fmtMoney(b.basic) },
      { label: "Passenger & Driver", value: fmtMoney((b.passenger || 0) + (b.driver || 0)) },
      { label: "Net Premium", value: fmtMoney(b.totalPremium) },
      { label: "VAT (15%)", value: fmtMoney(b.vat) },
      { label: "Final Premium", value: fmtMoney(b.final) },
    ].filter(f => f.value !== undefined && f.value !== null && f.value !== "");
  }



  // Matches the productType you are now sending from BikeForm
  if (item.productType === "MC Insurance" || item.productType === "Motor Insurance" || item.productType === "Commercial Vehicle Insurance") {
    return [
      { label: "Owner Name", value: d.ownerName },
      { label: "Vehicle Number", value: d.vehicleNumber },
      { label: "Sum Insured (FIV)", value: fmtMoney(d.fiv) },
      { label: "Engine Capacity", value: `${d.cc_capacity || d.cc} CC` },
      { label: "Mfg Year", value: d.mfgYear },
      { label: "Basic Premium", value: fmtMoney(d.BASIC_PREMIUM) },
      { label: "Own Damage (1.65%)", value: fmtMoney(d.FLV_ADD) },
      
      // Optional Risks - Only show if they have a value
      ...(d.FLOOD_COST > 0 ? [{ label: "Flood & Cyclone", value: fmtMoney(d.FLOOD_COST) }] : []),
      ...(d.EQ_COST > 0 ? [{ label: "Earthquake Cover", value: fmtMoney(d.EQ_COST) }] : []),
      
      // AVLS Discount
      ...(d.AVLS > 0 ? [{ label: "AVLS Discount (10%)", value: `- ${fmtMoney(d.AVLS)}` }] : []),
      
      { label: "OD Premium", value: fmtMoney(d.OD_PREMIUM) },
      { label: `NCB Discount (${d.ncb || 0}%)`, value: `- ${fmtMoney(d.NCB_LESS)}` },
      { label: "Sub Total", value: fmtMoney(d.SUB_TOTAL) },
      { label: "Motor Liability", value: fmtMoney(d.MOTOR_LIABILITY) },
      { label: "Passenger Liability", value: fmtMoney(d.PASSENGER_COST) },
      { label: "Paid Driver", value: fmtMoney(d.DRIVER_COST) },
      { label: "VAT (15%)", value: fmtMoney(d.VAT_AMOUNT) },
      { label: "Final Premium", value: fmtMoney(d.GROSS_PREMIUM) },
    ].filter(f => f.value !== undefined && f.value !== null && f.value !== "");
  }

  if (item.productType === "Travel Health Insurance") {
    // Extract country names from the array of objects
    const countries = d.visitingCountries?.map(c => c.label).join(", ");
    
    return [
      { label: "Traveller Name", value: d.passportName },
      { label: "Passport No", value: d.passportNumber },
      { label: "Plan Selected", value: `${d.plan} (${d.region})` },
      { label: "Destinations", value: countries },
      { label: "Travel Duration", value: `${d.travelDays} Days` },
      { label: "Travel Date", value: d.travelDate },
      { label: "Age", value: `${d.age} Yrs (${d.ageBand})` },
      { label: "Base Premium", value: fmtMoney(d.base) },
      { label: "VAT (15%)", value: fmtMoney(d.vat) },
      { label: "Total Payable", value: fmtMoney(d.total) },
    ].filter(f => f.value !== undefined && f.value !== null && f.value !== "");
  }

  // Fallback for other products (Apartment, etc.)
  if (item.productType === "Apartment Owner Insurance") {
    return [
      { label: "Owner", value: d.ownerName },
      { label: "Net Premium", value: fmtMoney(d.netPremium) },
      { label: "Total Premium", value: fmtMoney(d.final) },
    ];
  }

  return [];
};

/* ================================
   Main Cart Component
================================ */
const Cart = ({ cart, onRemove, onCheckout }) => {
  // If cart is empty, don't render anything
  if (!cart || cart.length === 0) return null;

  // Calculate the Grand Total of all items in the cart
  const grandTotal = cart.reduce((acc, item) => {
    const price = item.data?.GROSS_PREMIUM || item.data?.price || item.data?.final || 0;
    return acc + price;
  }, 0);

  return (
    <Box sx={{ mt: 4, mb: 8 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
        🛒 Your Cart <Box component="span" sx={{ fontSize: '14px', color: 'text.secondary', fontWeight: 400 }}>({cart.length} items)</Box>
      </Typography>

      <Grid container spacing={3}>
        {cart.map((item, index) => {
          const fields = getDisplayFields(item);
          const itemPrice = item.data?.GROSS_PREMIUM || item.data?.price || item.data?.final || 0;

          return (
            <Grid item xs={12} key={index}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  border: "1px solid #e0e0e0",
                  overflow: "hidden"
                }}
              >
                {/* Item Header */}
                <Box sx={{ display: "flex", justifyContent: "space-between", px: 3, py: 2, bgcolor: "#f8f9fa" }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#333" }}>
                    {item.productType || "Insurance Policy"}
                  </Typography>
                  <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 800 }}>
                    {fmtMoney(itemPrice)}
                  </Typography>
                </Box>

                <Divider />

                <CardContent sx={{ px: 3, py: 2 }}>
                  {/* Detailed Fields List */}
                  <Box component="dl" sx={{ m: 0 }}>
                    {fields.map((field, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          py: 0.8,
                          borderBottom: idx === fields.length - 1 ? "none" : "1px dashed #eee",
                        }}
                      >
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {field.label}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>
                          {field.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Actions */}
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, pt: 1, borderTop: '1px solid #f0f0f0' }}>
                    <Button 
                      size="small" 
                      color="error" 
                      variant="text" 
                      onClick={() => onRemove(index)}
                      sx={{ textTransform: 'none' }}
                    >
                      Remove Item
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Checkout Summary Card */}
      <Card 
        sx={{ 
          mt: 4, 
          p: 3, 
          borderRadius: 3, 
          bgcolor: "#2563eb", 
          color: "white",
          boxShadow: "0 10px 15px -3px rgba(37, 99, 235, 0.3)" 
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>Total Amount Payable:</Typography>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            {fmtMoney(grandTotal)}
          </Typography>
        </Box>
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={onCheckout}
          sx={{ 
            bgcolor: "white", 
            color: "#2563eb", 
            fontWeight: 800,
            fontSize: "1.1rem",
            py: 1.5,
            "&:hover": { bgcolor: "#f8f9fa" }
          }}
        >
          Proceed to Checkout
        </Button>
      </Card>
    </Box>
  );
};

export default Cart;